import { TDrawEntry, TDrawResult } from '@daymade/api-contracts';
import BackButton from 'components/BackButton';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import RevealAnimation from 'components/reveal/RevealAnimation';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { drawApiClient } from 'utils/apiClient';
import { stringFromDate } from 'utils/common';
import HttpStatusCode from 'utils/HttpStatusCode';

const Reveal = observer(
  ({
    result,
    usingBonusBall,
  }: {
    result: TDrawResult;
    usingBonusBall: boolean;
  }) => {
    const drawDate = result?.draw.drawDate
      ? stringFromDate(new Date(result?.draw.drawDate))
      : null;
    const [entries, setEntries] = useState<TDrawEntry[]>();
    const userStore = useUserStore();

    useEffect(() => {
      if (!userStore.currentUser?.id || !userStore.initialized) {
        return;
      }
      const loadAsync = async () => {
        if (result?.draw.id) {
          const drawEntries =
            await drawApiClient.auth.getDrawEntriesWithRealAndHypotheticalPrizes(
              {
                params: {
                  draw_id: result.draw.id,
                },
              },
            );

          if (drawEntries.status === HttpStatusCode.OK) {
            setEntries(drawEntries.body);
          }
        }
      };
      loadAsync();
    }, [result.draw.id, userStore.currentUser?.id, userStore.initialized]);

    return (
      <MainLayout>
        <NextSeo
          title={`Draw Reveal (${drawDate}) | Win Epic Prizes | DAYMADE`}
          openGraph={{
            title: `Draw Reveal (${drawDate}) | Win Epic Prizes | DAYMADE`,
          }}
        />

        <PageTitle>
          Draw <strong>reveal</strong>
        </PageTitle>

        <PaddedMaxWidthContainer>
          <RevealAnimation
            // @ts-ignore
            entries={entries}
            drawResult={result}
            usingBonusBall={usingBonusBall}
          />

          <Link href="/results" passHref>
            <BackButton>Back to all results</BackButton>
          </Link>
        </PaddedMaxWidthContainer>
      </MainLayout>
    );
  },
);

export const getServerSideProps = async (ctx: any) => {
  const redirectToResults = () => {
    return {
      redirect: {
        destination: `/results`,
        permanent: false,
      },
    };
  };

  const drawId = ctx.query.drawId;
  if (!drawId) {
    // NOTE - this should possibly return the result of redirectToResults, however, no one has reported the issue
    redirectToResults();
  }

  const getDrawResult = await drawApiClient.public.getDrawResult({
    query: { draw_id: drawId },
  });
  if (getDrawResult.status !== HttpStatusCode.OK) {
    // NOTE - this should possibly return the result of redirectToResults, however, no one has reported the issue
    redirectToResults();
  }

  const drawResult: TDrawResult = JSON.parse(
    JSON.stringify(getDrawResult.body),
  );

  if (!drawResult || !drawResult.draw.id) {
    // NOTE - this should possibly return the result of redirectToResults, however, no one has reported the issue
    redirectToResults();
  }

  let usingBonusBall: boolean = false;
  const drawRanges = await drawApiClient.public.getDrawRangesByDrawId({
    params: { draw_id: drawResult.draw.id },
  });
  if (getDrawResult.status === HttpStatusCode.OK) {
    usingBonusBall = JSON.parse(JSON.stringify(drawRanges.body.usingBonusBall));
  }

  return {
    props: {
      result: drawResult,
      usingBonusBall,
    },
  };
};

export default Reveal;
